import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { P } from '@hrms/shared';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { useBreakpoint, useDebounced } from '../lib/hooks';
import { Avatar, CommandPalette, Icon, IconButton, ListItem, Modal, radius, ShellContext, Text, useTheme, type PaletteItem, type ShellValue } from '../ui';
import { useCurrentRoute } from './currentRoute';
import { go } from './nav';
import { navSections, screenByName, SCREENS, isPermitted } from './registry';
import { TABS } from './tabs';
import { SECTIONS } from './types';

/** Registry name of the HR employee detail screen (opened from command-palette employee search). */
export const EMPLOYEE_DETAIL_SCREEN = 'EmployeeDetail';

function Sidebar() {
  const { colors } = useTheme();
  const { can, me } = useAuth();
  const current = useCurrentRoute();
  const groups = navSections(can);
  return (
    <View style={{ width: 248, backgroundColor: colors.sidebar, borderRightWidth: 1, borderRightColor: colors.border }} accessibilityRole="menu">
      <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: colors.border }}>
        <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="logoMark" size={16} color={colors.onPrimary} strokeWidth={3} />
        </View>
        <View style={{ flex: 1 }}>
          <Text weight="bold">Helios HRMS</Text>
          <Text variant="small" numberOfLines={1}>
            {me?.tenant?.name}
          </Text>
        </View>
      </View>
      <ScrollView contentContainerStyle={{ padding: 10, gap: 14 }} testID="sidebar">
        {groups.map((g) => (
          <View key={g.key} style={{ gap: 2 }}>
            <Text variant="caption" style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
              {g.label}
            </Text>
            {g.items.map((s) => {
              const active = current === s.name;
              return (
                <Pressable
                  key={s.name}
                  testID={`nav-${s.name}`}
                  accessibilityRole="link"
                  accessibilityState={{ selected: active }}
                  onPress={() => go(s.name)}
                  style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, paddingVertical: 8, borderRadius: radius.md, backgroundColor: active ? colors.primarySoft : pressed || hovered ? colors.hover : 'transparent' })}
                >
                  <Icon name={s.icon} size={16} color={active ? colors.primary : colors.muted} />
                  <Text size={13} weight={active ? 'semibold' : 'medium'} color={active ? colors.primary : colors.text} numberOfLines={1}>
                    {s.title}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

function UserMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, can, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const item = (fn: () => void) => () => {
    onClose();
    fn();
  };
  return (
    <Modal open={open} onClose={onClose} width={340} noScroll>
      <ListItem left={<Avatar name={me?.name ?? '?'} size={40} />} title={me?.name ?? ''} subtitle={`${me?.email ?? ''}\n${me?.tenant?.name ?? ''}`} />
      {can(P.SELF_SERVICE) && <ListItem icon="user" title="My profile" onPress={item(() => go('MyProfile'))} />}
      <ListItem icon={dark ? 'sun' : 'moon'} title={dark ? 'Light mode' : 'Dark mode'} onPress={item(toggle)} />
      <ListItem icon="logout" title="Sign out" onPress={item(() => void logout())} divider={false} testID="menu-logout" />
    </Modal>
  );
}

function TopBar({ onSearch, unread }: { onSearch: () => void; unread: number }) {
  const { colors, dark, toggle } = useTheme();
  const { me } = useAuth();
  const [menu, setMenu] = useState(false);
  const current = useCurrentRoute();
  const def = current ? screenByName(current) : undefined;
  const section = def ? SECTIONS.find((s) => s.key === def.section)?.label : undefined;
  const mac = Platform.OS === 'web' && typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 20, backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }}>
      <Text variant="muted" numberOfLines={1} style={{ minWidth: 120 }}>
        {section ? `${section} / ` : ''}
        <Text weight="medium">{def?.title ?? ''}</Text>
      </Text>
      <View style={{ flex: 1, alignItems: 'center' }}>
        <Pressable
          testID="topbar-search"
          accessibilityRole="search"
          accessibilityLabel="Open command palette"
          onPress={onSearch}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%', maxWidth: 440, height: 36, paddingHorizontal: 12, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface2 }}
        >
          <Icon name="search" size={16} color={colors.faint} />
          <Text color={colors.faint} style={{ flex: 1 }} numberOfLines={1}>
            Search screens, employees…
          </Text>
          <View style={{ paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4, borderWidth: 1, borderColor: colors.border }}>
            <Text size={11} color={colors.muted}>
              {mac ? '⌘K' : 'Ctrl K'}
            </Text>
          </View>
        </Pressable>
      </View>
      <IconButton icon={dark ? 'sun' : 'moon'} label="Toggle theme" onPress={toggle} />
      <IconButton icon="bell" label="Notifications" badge={unread} onPress={() => go('Notifications')} testID="topbar-bell" />
      <Pressable testID="user-menu" accessibilityRole="button" accessibilityLabel="User menu" onPress={() => setMenu(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Avatar name={me?.name ?? '?'} size={30} />
        <Text weight="medium" numberOfLines={1} style={{ maxWidth: 160 }}>
          {me?.name}
        </Text>
        <Icon name="chevronDown" size={14} color={colors.muted} />
      </Pressable>
      <UserMenu open={menu} onClose={() => setMenu(false)} />
    </View>
  );
}

/**
 * Authenticated app chrome: sidebar + top bar on wide screens (>= 1024), nothing extra on phones
 * (bottom tabs come from the navigator). Also owns the command palette and ShellContext.
 */
export function Shell({ children }: { children: React.ReactNode }) {
  const { isWide } = useBreakpoint();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { can } = useAuth();
  const [palette, setPalette] = useState(false);
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 250);

  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.ess.notifications({ unread: 1, per_page: 1 }),
    refetchInterval: 60_000,
  });
  const unread = unreadQ.data?.total ?? 0;

  const canSearchPeople = can(P.EMPLOYEES_VIEW, P.EMPLOYEES_MANAGE, P.EMPLOYEES_TEAM);
  const people = useQuery({
    queryKey: ['employees', 'palette', dq],
    queryFn: () => api.employees.list({ search: dq, per_page: 8 }),
    enabled: palette && canSearchPeople && dq.length >= 2,
  });

  const openPalette = useCallback(() => setPalette(true), []);
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const items = useMemo<PaletteItem[]>(
    () =>
      SCREENS.filter((s) => !s.hidden && isPermitted(s, can)).map((s) => ({
        key: `screen:${s.name}`,
        title: s.title,
        group: SECTIONS.find((x) => x.key === s.section)?.label ?? '',
        icon: s.icon,
        keywords: s.keywords,
        onSelect: () => go(s.name),
      })),
    [can],
  );
  const hasDetail = !!screenByName(EMPLOYEE_DETAIL_SCREEN);
  const extraItems = useMemo<PaletteItem[]>(
    () =>
      (people.data?.data ?? []).map((e) => ({
        key: `emp:${e.id}`,
        title: e.full_name,
        subtitle: [e.emp_code, e.designation, e.department].filter(Boolean).join(' · '),
        group: 'Employee',
        icon: 'user' as const,
        onSelect: () => (hasDetail ? go(EMPLOYEE_DETAIL_SCREEN, { id: e.id }) : undefined),
      })),
    [people.data, hasDetail],
  );

  const tabRoots = useMemo(() => new Set(TABS.map((t) => t.name).concat('More')), []);
  const topLevel = useMemo(() => new Set(SCREENS.filter((s) => !s.hidden).map((s) => s.name)), []);
  const value = useMemo<ShellValue>(() => ({ isWide, tabRoots, topLevel, openPalette, openNotifications: () => go('Notifications'), unread, go }), [isWide, tabRoots, topLevel, openPalette, unread]);

  return (
    <ShellContext.Provider value={value}>
      {isWide ? (
        <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.bg, paddingTop: insets.top }}>
          <Sidebar />
          <View style={{ flex: 1, minWidth: 0 }}>
            <TopBar onSearch={openPalette} unread={unread} />
            <View style={{ flex: 1 }}>{children}</View>
          </View>
        </View>
      ) : (
        <View style={{ flex: 1, backgroundColor: colors.bg }}>{children}</View>
      )}
      <CommandPalette open={palette} onClose={() => setPalette(false)} items={items} extraItems={extraItems} onQueryChange={setQ} />
    </ShellContext.Provider>
  );
}
